// Preview side of the message templates. This mirrors the shell and the
// placeholder filling that the edge functions use, so what an admin sees here
// is what a member receives.

export const MESSAGE_PLACEHOLDERS = [
  { token: "{{first_name}}", what: "The member's first name" },
  { token: "{{una_name}}", what: "Their association name" },
  { token: "{{email}}", what: "Their email address" },
  { token: "{{portal_url}}", what: "The portal web address" },
  { token: "{{tracking_number}}", what: "The package tracking number" },
  { token: "{{note}}", what: "The note the team wrote, on name messages" },
  { token: "{{action_url}}", what: "The one time link the member clicks" },
];

export const SAMPLE_VARS: Record<string, string> = {
  first_name: "Sample",
  una_name: "Sample Stewardship Association",
  email: "member@example.com",
  portal_url: "https://wlthystwrd.com",
  tracking_number: "1Z999AA10123456784",
  note: "This is where a note from the team appears.",
  action_url: "https://wlthystwrd.com/set-password",
};

/** Heading each message carries above its paragraphs. */
export const MESSAGE_HEADINGS: Record<string, string> = {
  name_approved: "Good news",
  name_change_requested: "One quick change",
  name_reopened: "About your Association's name",
  milestone_ready: "We have what we need",
  milestone_ein_acquired: "Your tax number is here",
  milestone_filings_submitted: "Filed with the state",
  milestone_filings_received: "Approved by the state",
  milestone_docs_sent: "On the way to you",
  milestone_docs_delivered: "Delivered",
  affiliation_advance: "One month out",
  affiliation_day_of: "Today is the day",
  affiliation_grace: "Whenever you are ready",
  docs_scan_nudge: "A quick check in",
  name_review_sla: "Name review is overdue",
  portal_invite: "Your portal is ready",
};

export const MESSAGE_LABELS: Record<string, string> = {
  name_approved: "Name approved",
  name_change_requested: "Name change requested",
  name_reopened: "Name reopened",
  milestone_ready: "Milestone: we have everything",
  milestone_ein_acquired: "Milestone: tax number",
  milestone_filings_submitted: "Milestone: filed with the state",
  milestone_filings_received: "Milestone: state approved",
  milestone_docs_sent: "Milestone: documents shipped",
  milestone_docs_delivered: "Milestone: documents delivered",
  affiliation_advance: "Affiliation anniversary, one month out",
  affiliation_day_of: "Affiliation anniversary, today",
  affiliation_grace: "Affiliation lapsed, gentle note",
  docs_scan_nudge: "Nudge: packet arrived, please scan",
  name_review_sla: "Team alert: a name is waiting",
  portal_invite: "Portal invitation for an existing member",
};

/** Which group each message belongs to in the editor. */
export type MessageGroup = "onboarding" | "fulfillment" | "system";

export const MESSAGE_GROUPS: Record<string, MessageGroup> = {
  name_approved: "onboarding",
  name_change_requested: "onboarding",
  name_reopened: "onboarding",
  milestone_ready: "onboarding",
  milestone_ein_acquired: "fulfillment",
  milestone_filings_submitted: "fulfillment",
  milestone_filings_received: "fulfillment",
  milestone_docs_sent: "fulfillment",
  milestone_docs_delivered: "fulfillment",
  docs_scan_nudge: "fulfillment",
  affiliation_advance: "system",
  affiliation_day_of: "system",
  affiliation_grace: "system",
  name_review_sla: "system",
  portal_invite: "system",
};

/** The fields each message can drop in. Anything else renders as nothing. */
export const MESSAGE_FIELDS: Record<string, string[]> = {
  name_approved: ["first_name", "una_name", "email", "portal_url"],
  name_change_requested: ["first_name", "una_name", "email", "note"],
  name_reopened: ["first_name", "una_name", "email", "portal_url", "note"],
  milestone_ready: ["first_name", "una_name", "email", "portal_url"],
  milestone_ein_acquired: ["first_name", "una_name", "email", "portal_url"],
  milestone_filings_submitted: ["first_name", "una_name", "email", "portal_url"],
  milestone_filings_received: ["first_name", "una_name", "email", "portal_url"],
  milestone_docs_sent: ["first_name", "una_name", "email", "portal_url", "tracking_number"],
  milestone_docs_delivered: ["first_name", "una_name", "email", "portal_url"],
  docs_scan_nudge: ["first_name", "email", "portal_url"],
  affiliation_advance: ["first_name", "una_name", "email", "portal_url"],
  affiliation_day_of: ["first_name", "una_name", "email", "portal_url"],
  affiliation_grace: ["first_name", "una_name", "email", "portal_url"],
  name_review_sla: ["first_name", "una_name", "note"],
  portal_invite: ["first_name", "una_name", "email", "portal_url", "action_url"],
};

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export function fillTokens(text: string, vars: Record<string, string>): string {
  return text.replace(TOKEN_RE, (_m, name: string) => vars[name] ?? "");
}

/** Same shell the sending code uses. */
export function emailShell(heading: string, paragraphs: string[]): string {
  const body = paragraphs
    .map((p) => `<p style="margin:0 0 14px;line-height:1.6">${p}</p>`)
    .join("");
  return `<div style="font-family:Georgia,serif;color:#1f2937;max-width:560px">
  <h1 style="font-size:20px;margin:0 0 16px">${heading}</h1>
  ${body}
  <p style="margin-top:24px;font-size:13px;color:#6b7280">Wealthy Steward</p>
</div>`;
}

export function renderPreview(key: string, body: string): string {
  const paragraphs = body
    .split(/\n{1,}/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => fillTokens(line, SAMPLE_VARS));
  return emailShell(fillTokens(MESSAGE_HEADINGS[key] ?? "", SAMPLE_VARS), paragraphs);
}

/** House style has no dashes. Returns true when the text carries one. */
export function hasFancyDash(text: string): boolean {
  return /[\u2013\u2014]/.test(text);
}

export const DASH_MESSAGE = "Dashes are not house style. Use a period or a comma.";
